import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  ClipboardDocumentIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../PMDialog";
import { parseImportedGuideFlow, serializeGuideFlowForExport } from "./guideFlowImportUtils";
import { PROMANG_GUIDE_FLOW_AI_GUIDE } from "./guideFlowAiGuide";

/**
 * View/edit guide flow JSON + copy AI authoring instructions.
 */
export function GuideFlowJsonDialog({
  open,
  onClose,
  flow,
  fallbackQuestions = [],
  onApplyNormalizedFlow,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("json");
  const [draft, setDraft] = useState("");
  const [parseError, setParseError] = useState("");
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current) {
      setDraft(serializeGuideFlowForExport(flow));
      setParseError("");
      setTab("json");
    }
    prevOpen.current = open;
  }, [open, flow]);

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      toast.success(t("guide.flow.copy_json_success"));
    } catch {
      toast.error(t("guide.flow.clipboard_failed"));
    }
  };

  const handleCopyAiGuide = async () => {
    try {
      await navigator.clipboard.writeText(PROMANG_GUIDE_FLOW_AI_GUIDE);
      toast.success(t("guide.flow.ai_guide_copied"));
    } catch {
      toast.error(t("guide.flow.clipboard_failed"));
    }
  };

  const handleResetDraft = () => {
    setDraft(serializeGuideFlowForExport(flow));
    setParseError("");
    toast.success(t("guide.flow.reload_from_canvas_success"));
  };

  const handleApply = () => {
    try {
      const normalized = parseImportedGuideFlow(draft, fallbackQuestions);
      onApplyNormalizedFlow(normalized);
      toast.success(t("guide.flow.json_applied"));
      onClose();
    } catch (e) {
      const msg = e?.message || String(e);
      setParseError(msg);
      toast.error(t("guide.flow.json_invalid", { message: msg }));
    }
  };

  return (
    <PMDialog open={open} onClose={onClose} size="4xl" title={t("guide.flow.json_dialog_title")} zIndex={4000}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
          <button
            type="button"
            onClick={() => setTab("json")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "json" ? "bg-sky-100 text-sky-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <DocumentTextIcon className="h-5 w-5 shrink-0" />
            {t("guide.flow.json_tab")}
          </button>
          <button
            type="button"
            onClick={() => setTab("ai")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "ai" ? "bg-slate-100 text-slate-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <BookOpenIcon className="h-5 w-5 shrink-0" />
            {t("guide.flow.ai_guide_tab")}
          </button>
        </div>

        {tab === "json" ? (
          <>
            <p className="text-sm text-gray-600">{t("guide.flow.json_hint")}</p>
            <textarea
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setParseError("");
              }}
              spellCheck={false}
              className="min-h-[min(420px,50vh)] w-full flex-1 rounded-lg border border-gray-300 font-mono text-xs leading-relaxed focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
            />
            {parseError ? (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{parseError}</p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCopyJson}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
                {t("guide.flow.copy_json")}
              </button>
              <button
                type="button"
                onClick={handleResetDraft}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-5 w-5" />
                {t("guide.flow.reload_from_canvas")}
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
              >
                {t("guide.flow.apply_json")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">{t("guide.flow.ai_guide_intro")}</p>
            <pre className="max-h-[min(420px,55vh)] overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-left font-mono text-xs leading-relaxed text-slate-800">
              {PROMANG_GUIDE_FLOW_AI_GUIDE}
            </pre>
            <button
              type="button"
              onClick={handleCopyAiGuide}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
              {t("guide.flow.copy_ai_guide")}
            </button>
          </>
        )}
      </div>
    </PMDialog>
  );
}
